import { useMemo } from 'react';

import type { DropdownItemBase } from '@/components/Autocomplete/autocomplete.types';
import { ReferenceType } from '@/core/graphql/generated/types';

import {
	type GetAllPicklistsQuery,
	useGetAllPicklistsQuery
} from '../graphql/query/GetAllPicklists.hook';
import type { PicklistOption, Picklists, ReferenceOptions } from '../picklists';

type Props = {
	enabled: boolean;
	countryId: string | null | undefined;
};

const RELATIONSHIP_TYPE_ORDER = ['WRKS', 'RLTD', 'LEDS', 'OWNSP', 'UNKD'];

const getLowerBound = (name: string): number => {
	const match = name.replace(/,/g, '').match(/^(\d+)/);
	return match ? parseInt(match[1], 10) : 0;
};

const rankRelationshipType = (id: string | undefined) => {
	const index = RELATIONSHIP_TYPE_ORDER.indexOf(id ?? '');
	return index === -1 ? RELATIONSHIP_TYPE_ORDER.length : index;
};

const isReferenceResult = (
	data: unknown
): data is { __typename: 'ReferenceResult'; results: PicklistOption[] } =>
	typeof data === 'object' &&
	data !== null &&
	'__typename' in data &&
	data.__typename === 'ReferenceResult' &&
	'results' in data;

export const buildYearOptions = (
	start = 1900,
	end = new Date().getFullYear()
) => {
	const years: { id: string; name: string }[] = [];
	for (let y = end; y >= start; y -= 1) {
		years.push({ id: String(y), name: String(y) });
	}
	return years;
};

const extractCountriesAndStates = (
	countries: GetAllPicklistsQuery['countries'] | undefined
) => {
	const countryOptions: DropdownItemBase[] = [];
	const stateOptionsDictionary: Record<string, DropdownItemBase[]> = {};

	countries?.forEach(country => {
		if (!country?.id || !country.name) return;

		countryOptions.push({ id: country.id, name: country.name });
		stateOptionsDictionary[country.id] = (country.states ?? []).flatMap(
			state =>
				state?.id && state.name ? [{ id: state.id, name: state.name }] : []
		);
	});

	return { countryOptions, stateOptionsDictionary };
};

export const usePicklistValues = ({ enabled, countryId }: Props) => {
	const hasValidCountry = !!countryId && countryId.length >= 2;

	const { data, loading } = useGetAllPicklistsQuery({
		skip: !enabled || !countryId,
		variables: hasValidCountry ? { countryId } : undefined
	});

	const picklists = useMemo((): Picklists => {
		const referenceOptions = Object.fromEntries(
			(Object.keys(ReferenceType) as Array<keyof typeof ReferenceType>).map(
				key => {
					const result = data?.[ReferenceType[key]];
					return [
						`${key.charAt(0).toLowerCase()}${key.slice(1)}Options`,
						isReferenceResult(result) ? result.results : []
					];
				}
			)
		) as ReferenceOptions;

		referenceOptions.countRangeOptions = [
			...referenceOptions.countRangeOptions
		].sort((a, b) => getLowerBound(a.name) - getLowerBound(b.name));

		referenceOptions.relationshipTypeOptions = [
			...referenceOptions.relationshipTypeOptions
		].sort((a, b) => rankRelationshipType(a.id) - rankRelationshipType(b.id));

		return {
			...referenceOptions,
			...extractCountriesAndStates(data?.countries),
			yearOptions: buildYearOptions()
		};
	}, [data]);

	return { picklists, loading };
};
